// Cockpit Démo — Particulier (Sophie Martin)
// Page de démonstration pré-remplie pour montrer la valeur du cockpit aux visiteurs non connectés
// Tutoiement systématique — Français uniquement

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, FileText, Star, Bell, Settings, Plus, ChevronRight, MapPin, Phone, Mail, Calendar, Euro, Clock, CheckCircle, AlertCircle, User } from 'lucide-react';

// Mock data Sophie Martin
const SOPHIE_DATA = {
  user: {
    name: 'Sophie Martin',
    avatar: null,
    email: 'sophie.martin@email.fr',
    phone: '06 12 34 56 78',
    address: '12 Rue des Lilas, 69003 Lyon',
    memberSince: 'Mars 2024',
  },
  stats: {
    projetsEnCours: 2,
    devisRecus: 5,
    artisansFavoris: 8,
    messagesNonLus: 3,
  },
  projects: [
    {
      id: 1,
      title: 'Rénovation cuisine',
      status: 'en_cours',
      budget: '15 000 - 20 000 €',
      startDate: '15 Mars 2026',
      artisan: { name: 'Cuisines Durand', avatar: '👨‍🍳', rating: 4.8 },
      progress: 65,
      nextStep: 'Pose du plan de travail',
      nextDate: '22 Mars 2026',
    },
    {
      id: 2,
      title: 'Isolation combles',
      status: 'devis',
      budget: '8 000 - 12 000 €',
      devisCount: 3,
      deadline: '30 Mars 2026',
    },
  ],
  quotes: [
    { id: 1, artisan: 'Iso-Confort Lyon', project: 'Isolation combles', amount: 9500, status: 'en_attente', date: '10 Mars 2026' },
    { id: 2, artisan: 'Thermo Pro', project: 'Isolation combles', amount: 11200, status: 'en_attente', date: '12 Mars 2026' },
    { id: 3, artisan: 'EcoIsol', project: 'Isolation combles', amount: 8800, status: 'accepté', date: '8 Mars 2026' },
  ],
  favoriteArtisans: [
    { id: 1, name: 'Cuisines Durand', specialty: 'Cuisiniste', rating: 4.8, reviews: 127, avatar: '👨‍🍳' },
    { id: 2, name: 'Plomberie Martin', specialty: 'Plombier', rating: 4.9, reviews: 89, avatar: '🔧' },
    { id: 3, name: 'Elec Service', specialty: 'Électricien', rating: 4.7, reviews: 156, avatar: '⚡' },
    { id: 4, name: 'Peintures Pro', specialty: 'Peintre', rating: 4.6, reviews: 203, avatar: '🎨' },
  ],
  notifications: [
    { id: 1, type: 'devis', message: 'Nouveau devis reçu de Thermo Pro', time: 'Il y a 2h', unread: true },
    { id: 2, type: 'rdv', message: 'Rappel: RDV chantier cuisine demain 9h', time: 'Il y a 5h', unread: true },
    { id: 3, type: 'message', message: "Cuisines Durand t'a envoyé un message", time: 'Hier', unread: true },
  ],
};

export default function DemoParticulierPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  const getStatusColor = (status) => {
    switch (status) {
      case 'en_cours': return { bg: '#DCFCE7', text: '#166534', label: 'En cours' };
      case 'devis': return { bg: '#FEF3C7', text: '#92400E', label: 'En attente devis' };
      case 'termine': return { bg: '#E0E7FF', text: '#3730A3', label: 'Terminé' };
      case 'en_attente': return { bg: '#FEF3C7', text: '#92400E', label: 'En attente' };
      case 'accepté': return { bg: '#DCFCE7', text: '#166534', label: 'Accepté' };
      case 'refusé': return { bg: '#FEE2E2', text: '#991B1B', label: 'Refusé' };
      default: return { bg: '#F3F4F6', text: '#374151', label: status };
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: Home },
    { id: 'projects', label: 'Mes projets', icon: FileText },
    { id: 'quotes', label: 'Devis', icon: Euro },
    { id: 'favorites', label: 'Favoris', icon: Star },
  ];

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#F8FAFC',
      paddingBottom: '100px',
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #E8650A 0%, #F97316 100%)',
        padding: '16px 20px',
        paddingTop: 'max(16px, env(safe-area-inset-top))',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '10px',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            data-testid="back-button"
          >
            <ArrowLeft size={20} color="#fff" />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '20px',
              padding: '4px 12px',
              fontSize: '11px',
              fontWeight: '600',
              color: '#fff',
              marginBottom: '4px',
            }}>
              MODE DÉMO
            </div>
            <h1 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: '20px',
              fontWeight: '700',
              color: '#fff',
              margin: 0,
            }}>Cockpit Particulier</h1>
          </div>
          <Bell size={22} color="#fff" style={{ opacity: 0.9 }} />
        </div>

        {/* User Card */}
        <div style={{
          background: 'rgba(255,255,255,0.15)',
          borderRadius: '16px',
          padding: '16px',
          backdropFilter: 'blur(10px)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
            }}>👩</div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '16px',
                fontWeight: '700',
                color: '#fff',
              }}>{SOPHIE_DATA.user.name}</div>
              <div style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '12px',
                color: 'rgba(255,255,255,0.8)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
                <MapPin size={12} /> {SOPHIE_DATA.user.address.split(',')[1]}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '8px',
        padding: '16px',
        marginTop: '-8px',
      }}>
        {[
          { value: SOPHIE_DATA.stats.projetsEnCours, label: 'Projets', color: '#E8650A' },
          { value: SOPHIE_DATA.stats.devisRecus, label: 'Devis', color: '#3B82F6' },
          { value: SOPHIE_DATA.stats.artisansFavoris, label: 'Favoris', color: '#10B981' },
          { value: SOPHIE_DATA.stats.messagesNonLus, label: 'Messages', color: '#8B5CF6' },
        ].map((stat, idx) => (
          <div key={idx} style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '12px 8px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: '20px',
              fontWeight: '700',
              color: stat.color,
            }}>{stat.value}</div>
            <div style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '10px',
              color: '#6B7280',
            }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        padding: '0 16px',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 14px',
                borderRadius: '10px',
                border: 'none',
                background: isActive ? '#E8650A' : '#fff',
                color: isActive ? '#fff' : '#6B7280',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '13px',
                fontWeight: isActive ? '600' : '500',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                boxShadow: isActive ? '0 2px 8px rgba(232, 101, 10, 0.3)' : '0 1px 3px rgba(0,0,0,0.08)',
              }}
              data-testid={`tab-${tab.id}`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{ padding: '16px' }}>
        {activeTab === 'dashboard' && (
          <>
            {/* Notifications */}
            {SOPHIE_DATA.notifications.some(n => n.unread) && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1F2937',
                  marginBottom: '12px',
                }}>Notifications récentes</h3>
                {SOPHIE_DATA.notifications.filter(n => n.unread).map((notif) => (
                  <div key={notif.id} style={{
                    background: '#fff',
                    borderRadius: '12px',
                    padding: '14px',
                    marginBottom: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    borderLeft: '3px solid #E8650A',
                  }}>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '13px',
                      color: '#1F2937',
                      marginBottom: '4px',
                    }}>{notif.message}</div>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '11px',
                      color: '#9CA3AF',
                    }}>{notif.time}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Projets en cours */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1F2937',
                }}>Tes projets</h3>
                <button style={{
                  background: 'none',
                  border: 'none',
                  color: '#E8650A',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}>
                  <Plus size={14} /> Nouveau projet
                </button>
              </div>
              {SOPHIE_DATA.projects.map((project) => {
                const status = getStatusColor(project.status);
                return (
                  <div key={project.id} style={{
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '16px',
                    marginBottom: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <h4 style={{
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          fontSize: '15px',
                          fontWeight: '600',
                          color: '#1F2937',
                          margin: 0,
                        }}>{project.title}</h4>
                        <span style={{
                          display: 'inline-block',
                          marginTop: '6px',
                          background: status.bg,
                          color: status.text,
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600',
                        }}>{status.label}</span>
                      </div>
                      <ChevronRight size={20} color="#9CA3AF" />
                    </div>

                    {project.status === 'en_cours' && (
                      <>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          marginBottom: '12px',
                          padding: '10px',
                          background: '#F8FAFC',
                          borderRadius: '10px',
                        }}>
                          <div style={{ fontSize: '24px' }}>{project.artisan.avatar}</div>
                          <div>
                            <div style={{
                              fontFamily: "'DM Sans', sans-serif",
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#1F2937',
                            }}>{project.artisan.name}</div>
                            <div style={{
                              fontFamily: "'DM Sans', sans-serif",
                              fontSize: '11px',
                              color: '#6B7280',
                            }}>⭐ {project.artisan.rating}</div>
                          </div>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '11px', color: '#6B7280' }}>Avancement</span>
                            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '11px', fontWeight: '600', color: '#E8650A' }}>{project.progress}%</span>
                          </div>
                          <div style={{ height: '6px', background: '#E5E7EB', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ width: `${project.progress}%`, height: '100%', background: '#E8650A', borderRadius: '3px' }} />
                          </div>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: '12px',
                          color: '#6B7280',
                        }}>
                          <Calendar size={14} />
                          Prochaine étape: {project.nextStep} — {project.nextDate}
                        </div>
                      </>
                    )}

                    {project.status === 'devis' && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: '12px',
                        color: '#6B7280',
                      }}>
                        <FileText size={14} />
                        {project.devisCount} devis reçus • Budget: {project.budget}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Artisans favoris (aperçu) */}
            <div>
              <h3 style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '14px',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '12px',
              }}>Tes artisans favoris</h3>
              <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px' }}>
                {SOPHIE_DATA.favoriteArtisans.slice(0, 4).map((artisan) => (
                  <div key={artisan.id} style={{
                    background: '#fff',
                    borderRadius: '12px',
                    padding: '14px',
                    minWidth: '140px',
                    textAlign: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  }}>
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>{artisan.avatar}</div>
                    <div style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#1F2937',
                      marginBottom: '2px',
                    }}>{artisan.name}</div>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '11px',
                      color: '#6B7280',
                      marginBottom: '6px',
                    }}>{artisan.specialty}</div>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '11px',
                      color: '#F59E0B',
                    }}>⭐ {artisan.rating} ({artisan.reviews})</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'projects' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '16px',
                fontWeight: '600',
                color: '#1F2937',
              }}>Tous tes projets</h3>
              <button style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: '#E8650A',
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                padding: '10px 16px',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
              }}>
                <Plus size={16} /> Créer un projet
              </button>
            </div>
            {SOPHIE_DATA.projects.map((project) => {
              const status = getStatusColor(project.status);
              return (
                <div key={project.id} style={{
                  background: '#fff',
                  borderRadius: '16px',
                  padding: '16px',
                  marginBottom: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4 style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#1F2937',
                        margin: '0 0 6px 0',
                      }}>{project.title}</h4>
                      <span style={{
                        display: 'inline-block',
                        background: status.bg,
                        color: status.text,
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                      }}>{status.label}</span>
                    </div>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#E8650A',
                    }}>{project.budget}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'quotes' && (
          <div>
            <h3 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: '16px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '16px',
            }}>Tes devis</h3>
            {SOPHIE_DATA.quotes.map((quote) => {
              const status = getStatusColor(quote.status);
              return (
                <div key={quote.id} style={{
                  background: '#fff',
                  borderRadius: '16px',
                  padding: '16px',
                  marginBottom: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div>
                      <h4 style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1F2937',
                        margin: '0 0 4px 0',
                      }}>{quote.artisan}</h4>
                      <div style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: '12px',
                        color: '#6B7280',
                      }}>{quote.project}</div>
                    </div>
                    <span style={{
                      background: status.bg,
                      color: status.text,
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                    }}>{status.label}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#1F2937',
                    }}>{quote.amount.toLocaleString('fr-FR')} €</div>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '11px',
                      color: '#9CA3AF',
                    }}>{quote.date}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'favorites' && (
          <div>
            <h3 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: '16px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '16px',
            }}>Tes artisans favoris</h3>
            {SOPHIE_DATA.favoriteArtisans.map((artisan) => (
              <div key={artisan.id} style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '16px',
                marginBottom: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  background: '#F3F4F6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                }}>{artisan.avatar}</div>
                <div style={{ flex: 1 }}>
                  <h4 style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: '0 0 4px 0',
                  }}>{artisan.name}</h4>
                  <div style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '12px',
                    color: '#6B7280',
                    marginBottom: '4px',
                  }}>{artisan.specialty}</div>
                  <div style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '12px',
                    color: '#F59E0B',
                  }}>⭐ {artisan.rating} • {artisan.reviews} avis</div>
                </div>
                <button style={{
                  background: '#E8650A',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '8px 14px',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}>Contacter</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CTA Bottom */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#fff',
        borderTop: '1px solid #E5E7EB',
        padding: '12px 20px',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
        zIndex: 100,
      }}>
        <button
          onClick={() => navigate('/inscription')}
          style={{
            width: '100%',
            background: '#E8650A',
            color: '#fff',
            border: 'none',
            borderRadius: '14px',
            padding: '14px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: '15px',
            fontWeight: '700',
            cursor: 'pointer',
          }}
          data-testid="cta-creer-cockpit"
        >
          🏠 Crée ton Cockpit Particulier — Gratuit
        </button>
      </div>
    </div>
  );
}
